import {ReplayRounded, SearchRounded} from '@mui/icons-material';
import {Button, Checkbox, Grid, ListItemText, MenuItem, Paper, Stack, TextField, useTheme} from '@mui/material';
import {AgGridReact} from 'ag-grid-react';
import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import AgGridCustomTable from '../../../../../util/component/AgGridCustomTable';
import SectionHeader from '../../../../../util/component/SectionHeader';
import {ColDef, GridOptions, IServerSideGetRowsParams} from 'ag-grid-community';
import {useVoucherActions} from '../../../service/useMobilityVoucherAction';
import {SubscriptionAdvancedFilters, SubscriptionInputGetAll} from '../../../interfaces/Subscription';
import {useAgGridLocale} from '../../../../../config/globalConfig';
import {Controller, useForm} from 'react-hook-form';
import {useRecoilState} from 'recoil';
import {STEPPER_STATE, SUBSCRIPTION_SELECTED} from '../../../service/MobilityVoucherRecoil';
import {Category} from '../../../interfaces/Category';
import ContentBody from '../../../component/common/ContentBody';
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const SubscriptionStep = () => {
    const {t} = useTranslation(['mobilityvoucher']);
    const {t: tg} = useTranslation("translation");

    const agGridLocale = useAgGridLocale();
    const theme = useTheme();
    const {globalData: {user}, dispatchGlobalData} = useContext(GlobalContext);
    const [categories, setCategories] = useState<Category[]>([]);
    const {getCategories, getSubscription} = useVoucherActions();
    const gridRef = useRef<AgGridReact>(null);
    const [isDisabled, setIsDisabled] = useState(true);
    const [activeStep, setActiveStep] = useRecoilState(STEPPER_STATE);
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    const [dealers, setDealers] = React.useState<any[]>(dealerOptions);

    const filterDefaultValues: SubscriptionAdvancedFilters = {
        categoryName: [],
        productName: "",
        sku: "",
    };
    const defaultValues = {
        categoryName: new Array<string>(),
        productName: "",
        sku: "",
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [] as string[]
    };
    const [filters, setFilters] = useState<SubscriptionAdvancedFilters>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const [rowData, setRowData] = useState<any>([]);
    const [subSelect, setSubSelect] = useRecoilState(SUBSCRIPTION_SELECTED);
    const {
        control,
        handleSubmit,
        formState: {isValid},
        reset, getValues
    } = useForm({mode: "onChange", defaultValues});

    function createDatasource(filters: SubscriptionAdvancedFilters) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp: SubscriptionInputGetAll = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                try {
                    let data = await getSubscription(filterTemp);
                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                    data?.totalElements == 0 && params.api.showNoRowsOverlay();
                } catch (e) {
                    params.fail();
                    params.api.hideOverlay();
                }
            }
        };
    }

    const columns: ColDef[] = [
        {
            field: 'productCode',
            headerName: t('subscription_table.code'),
            checkboxSelection: true,
            lockPosition: true
        },
        {
            field: 'name',
            headerName: t('subscription_table.name')
        },
        {
            field: "category",
            headerName: t('subscription_table.category')
        },
        {
            field: 'sku',
            headerName: t('subscription_table.sku')
        },
        {
            field: 'entryFee',
            headerName: t('subscription_table.fee'),
            valueGetter: params => {
                return parseFloat(params.data.entryFee) / 100 + " €"
            }
        },
    ];

    const onSelectionChanged = () => {
        const selectedRows = gridOptions.api!.getSelectedRows();
        if (selectedRows.length) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    };

    const gridOptions: GridOptions = {
        rowSelection: 'single',
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: false,
            resizable: true,
            wrapHeaderText: true
        },
        columnDefs: columns,
        localeText: agGridLocale,
        onSelectionChanged: onSelectionChanged,
        onGridReady: (params) => {
            params.api.setServerSideDatasource(createDatasource(filters));
        },
        serverSideDatasource: createDatasource(filters)
    };

    const onFormSubmit = (data: any) => {
        gridRef.current?.api.deselectAll();
        setFilters(data);
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters));
    };

    const onErrors = (errors: any) => console.error(errors);

    const handleNext = async () => {
        setSubSelect(gridRef.current?.api.getSelectedRows());
        setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
    };

    const navigate = useNavigate();
    const handleCancel = async () => {
        setActiveStep((prevActiveStep: number) => 0);
        navigate('/mobilityvoucher/orders');
    };

    useEffect(() => {
        getCategories().then((res) => setCategories(res));
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters));
    }, [filters]);

    const resetForm = () => {
        reset(defaultValues);
        onFormSubmit({});
    }


    return (
        <>
            <Paper elevation={1} sx={{padding: "", margin: "18px"}}>
                <SectionHeader title={t('stepper.steps.title1')}
                               subtitle={t('stepper.steps.subtitle1')}/>
                <ContentBody>
                    <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                        <form onSubmit={handleSubmit(onFormSubmit, onErrors)} noValidate>
                            <Grid sx={{padding: "16px"}} alignItems="center" container spacing={2}>
                                <Grid item xs={3}>
                                    <Controller
                                        name='categoryName'
                                        control={control}
                                        render={({field: {ref, ...field}, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('mobilityvoucher:filterBox.category')}
                                                placeholder={t('mobilityvoucher:filterBox.category')}
                                                select
                                                SelectProps={{
                                                    multiple: true,
                                                    renderValue: (selected: any) => selected.join(', ')
                                                }}
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                    onFormSubmit(getValues())
                                                }
                                                }
                                            >
                                                {categories && categories?.map((category) => (
                                                    <MenuItem key={category.categoryName} value={category.categoryName}>
                                                        <Checkbox
                                                            checked={field.value.indexOf(category.categoryName) > -1}/>
                                                        <ListItemText primary={category.categoryName}/>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={3}>
                                    <Controller
                                        name='productName'
                                        control={control}
                                        render={({field: {ref, ...field}, fieldState}) => (
                                            <TextField
                                                label={t('mobilityvoucher:subscription_table.name')}
                                                placeholder={t('mobilityvoucher:subscription_table.name')}
                                                fullWidth
                                                defaultValue={''}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={3}>
                                    <Controller
                                        name='sku'
                                        control={control}
                                        render={({field: {ref, ...field}, fieldState}) => (
                                            <TextField
                                                label={t('mobilityvoucher:filterBox.sku')}
                                                placeholder={t('mobilityvoucher:filterBox.sku')}
                                                fullWidth
                                                defaultValue={''}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                            </TextField>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={3}>
                                    <Stack direction="row" alignItems="center" justifyContent="end" columnGap={2}>
                                        <Button variant="text" startIcon={<ReplayRounded/>} onClick={resetForm}>
                                            {t("searchTooltip.clearFilters")}
                                        </Button>
                                        <Button type="submit" size="icon" color="accent">
                                            <SearchRounded/>
                                        </Button>
                                    </Stack>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"legalEntities"}
                                        render={({field}) => (
                                            <TextField
                                                fullWidth
                                                label={tg("shared.legalEntities")}
                                                select
                                                SelectProps={{
                                                    multiple: true,
                                                    renderValue: (selected: any) =>
                                                        selected
                                                            .map((s: any) => legalEntityOptions.find((ss) => ss.value === s)!.label)
                                                            .join(", "),
                                                }}
                                                value={field.value}
                                                onChange={(event) => {
                                                    setDealers([]);
                                                    let dealers = user?.dealers
                                                        ?.filter(
                                                            (x: any) =>
                                                                event.target.value?.length > 0 &&
                                                                x.legalEntities.some((e: any) => event.target.value.includes(e.name)) &&
                                                                x.name.toLowerCase() !== "all"
                                                        )
                                                        .map((x: any) => {
                                                            return {
                                                                label: tg(`dealers.${x.name}`),
                                                                value: x.name,
                                                            };
                                                        });
                                                    setDealers(dealers);
                                                    field.onChange(event)
                                                }
                                                }
                                            >
                                                {legalEntityOptions.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>
                                                        <Checkbox checked={field.value?.indexOf(item.value) > -1}/>
                                                        <ListItemText primary={item.label}/>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"dealers"}
                                        render={({field}) => (
                                            <TextField
                                                fullWidth
                                                label={tg("shared.dealers")}
                                                select
                                                SelectProps={{
                                                    multiple: true,
                                                    renderValue: (selected: any) =>
                                                        selected
                                                            .map((s: any) => dealers.find((ss) => ss.value === s)!.label)
                                                            .join(", "),
                                                }}
                                                value={field.value}
                                                onChange={field.onChange}
                                            >
                                                {dealers.map((item) => (
                                                    <MenuItem key={item.value} value={item.value}>
                                                        <Checkbox checked={field.value?.indexOf(item.value) > -1}/>
                                                        <ListItemText primary={item.label}/>
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>

                            </Grid>
                        </form>
                    </Paper>
                </ContentBody>
                <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef}/>
            </Paper>
            <Grid sx={{margin: '18px'}} item xs={12} display={"flex"} justifyContent={"flex-end"} mt={3}>
                <Button onClick={handleCancel} size='medium' variant="outlined"
                        sx={{mr: 1}}> {t("stepper.back")} </Button>
                <Button disabled={isDisabled} onClick={handleNext} size='medium'> {t("stepper.next")}</Button>
            </Grid>
        </>
    );
};

export default SubscriptionStep;